import React, { useCallback, useEffect } from 'react';
import { connect, shallowEqual, useDispatch, useSelector } from 'react-redux';
import { withMountListener } from '@beef/react';
import { useCustomEvent, useWindowSetter } from '@beef/shop-common';
import { compose } from 'redux';

import { COOKIES_NAMES } from 'constants/cookies';
import { MODAL_TYPE } from 'constants/modal';
import { advCakeEvents } from 'pushEvent/advCakeEvents';
import { cookieBasketService } from 'services';
import { getBasketData } from 'store/common/basket/actions';
import {
  getBasketData as getBasketDataSelector,
  isBasketDataFetchedSelector,
} from 'store/common/basket/selectors';
import { selectIsMobileView } from 'store/common/mobileView/selectors';
import { toggleModal } from 'store/common/modal/actions';
import { getPageIsSearch } from 'store/common/navigation/selectors';
import { onProductCatalogMount } from 'store/ProductCatalog/actions';
import { getCatalogDisableFilters } from 'store/ProductCatalog/controls/selectors';
import {
  fetchDeviceDetailsData,
  setDeviceDetailsIsCompareData,
  setDeviceDetailsIsFavoriteData,
} from 'store/ProductCatalog/detailsModal/actions';
import { getCatalogFiltersAvailability } from 'store/ProductCatalog/filters/selectors';
import {
  setIsCompareCatalogDevices,
  setIsFavoriteCatalogDevices,
} from 'store/ProductCatalog/products/actions';
import {
  getCataloMainCollectionIdSelectors,
  getCataloMainCollectionNameSelectors,
  getCatalogProductsListRaw,
  getCatalogProductsNotEmpty,
} from 'store/ProductCatalog/products/selectors';
import rootReducer from 'store/ProductCatalog/reducers';
import { postViewedProduct } from 'store/widgets/recentlyViewedProducts/actions';
import { getThreeFoursAvailability } from 'store/widgets/ThreeFours/selectors';
import connectProvider from 'utils/redux-provider';
import { PartnerOrder, SubscribeForm, threeFours } from 'widgets';
import { ThreeFoursModal } from 'widgets/ThreeFours';
import { safeLazy } from 'utils/safeLazy';

import PageLayout from '../_PageLayout';
import { SeoPreFooterWidget } from './components';
import { convertLegacyModel } from './dataConverter';
import MainView from './views/Main';

const AlgoliaSearch = safeLazy(
  () =>
    import(
      /* webpackChunkName: "AlgoliaSearch" */
      './components/AlgoliaSearch'
    ),
);

/**
 * Full Product Catalog page, connected to store and containing layout
 */
const ProductCatalogPage = () => {
  const dispatch = useDispatch();
  const { subscribeDOM } = useCustomEvent();

  const getRecentlyViewedProducts = useCallback(() => dispatch(postViewedProduct()), [dispatch]);

  const [setWindowFunc] = useWindowSetter({
    previewProduct: (slug) => {
      dispatch(fetchDeviceDetailsData(slug));
      dispatch(toggleModal(MODAL_TYPE.DETAILS_DEVICE));
    },
  });

  useEffect(() => {
    setWindowFunc();
    getRecentlyViewedProducts();
    cookieBasketService.updateBasketIdCookie();
  }, []);

  const {
    basketData,
    catalogProducts,
    disableFilters,
    filtersAvailability,
    hasThreeFours,
    isBasketDataFetched,
    isMobileView,
    isSearchPage,
    mainCollectionId,
    mainCollectionName,
  } = useSelector(
    (state) => ({
      basketData: getBasketDataSelector(state),
      catalogProducts: getCatalogProductsListRaw(state),
      disableFilters: !getCatalogDisableFilters(state),
      filtersAvailability: getCatalogFiltersAvailability(state),
      hasThreeFours: getThreeFoursAvailability(state),
      isBasketDataFetched: isBasketDataFetchedSelector(state),
      isEmptyCatalog: !getCatalogProductsNotEmpty(state),
      isMobileView: selectIsMobileView(state),
      isSearchPage: getPageIsSearch(state),
      mainCollectionId: getCataloMainCollectionIdSelectors(state),
      mainCollectionName: getCataloMainCollectionNameSelectors(state),
    }),
    shallowEqual,
  );

  useEffect(() => {
    getBasketData();
  }, []);

  useEffect(() => {
    if (isBasketDataFetched) {
      advCakeEvents.setEventForCatalog({
        basketData,
        category: {
          id: mainCollectionId,
          name: mainCollectionName,
        },
        productList: catalogProducts,
      });
    }
  }, [isBasketDataFetched]);

  useEffect(() => {
    const unSubscribeFavorites = subscribeDOM(COOKIES_NAMES.FAVORITE_DEVICES, (favoritesList) => {
      dispatch(setIsFavoriteCatalogDevices(favoritesList));
      dispatch(setDeviceDetailsIsFavoriteData(favoritesList));
    });

    const unSubscribeCmp = subscribeDOM(COOKIES_NAMES.COMPARISON_DEVICES, (cmpList) => {
      dispatch(setIsCompareCatalogDevices(cmpList));
      dispatch(setDeviceDetailsIsCompareData(cmpList));
    });

    return () => {
      unSubscribeFavorites();
      unSubscribeCmp();
    };
  }, []);

  if (isSearchPage) {
    return (
      <PageLayout>
        <AlgoliaSearch />
      </PageLayout>
    );
  }
  const shouldShowSeoPreFooterWidget = filtersAvailability && disableFilters && !isMobileView;

  return (
    <PageLayout beforeContent={hasThreeFours && <ThreeFoursModal />}>
      <MainView />
      {shouldShowSeoPreFooterWidget && <SeoPreFooterWidget />}
    </PageLayout>
  );
};

/**
 * Standalone connected Product Catalog Page
 */

const mapDispatchToProps = {
  onMount: onProductCatalogMount,
};

const ProductCatalogPage_ = compose(
  connect(null, mapDispatchToProps),
  withMountListener,
)(ProductCatalogPage);

export default connectProvider(rootReducer, convertLegacyModel, {
  SubscribeForm,
  partnerSettings: PartnerOrder,
  threeFours,
})(ProductCatalogPage_);
